import {
  IDetailOverlayRow as IRow,
  convertOldRegionsToNewRegions
} from '../../viz/Utils';
import {
  colorRangeEnd,
  colorRangeStart,
  colorSpectrumEndAsPercentageOfTotalOrdersOfMagnitudeDifference as spectrumEndPct,
  colorSpectrumStartAsPercentageOfTotalOrdersOfMagnitudeDifference as spectrumStartPct,
  getOrdersOfMagnitudeDifference,
  IComputationOutput,
  ICountryDatum,
  ICountryDatumDisjoint,
  IdGetter,
  ILegendData,
} from './Utils';

import {
  scaleLog,
} from 'd3-scale';
import keyBy from 'lodash-es/keyBy';
import sum from 'lodash-es/sum';
import {
  TradeDirection,
  TradeFlow,
  QueryLevel
} from '../../graphQL/types/shared';
import {
  formatPercentage,
  formatTradeValue,
} from '../../numberFormatters';
import {
  groupedRegionColorMap,
  regionColorMap,
} from '../../Utils';
import {
  IMetadatum,
} from '../fetchedData/countryMetadata';
import getTradeLabel from '../rings/getTradeLabel';

interface IDatumWithoutPct {
  id: number;
  value: number;
}

interface IImportExport {
  export_value: number;
  import_value: number;
}
type ValueGetter<T extends IImportExport> = (input: T) => number;

const assignValueAndPct = <Datum extends IImportExport>(
    getId: IdGetter<Datum>,
    getValue: ValueGetter<Datum>,
    data: Datum[],
    primaryCountryId: number | undefined,
    metadata: Map<number, IMetadatum>,
    levelMapping: Map<number, number>,
    tradeDirection: TradeDirection,
    tradeFlow: TradeFlow,
    year: number,
    width: number,
    height: number,
    queryLevel: QueryLevel,
    groupsMetadata: [],
    groupMembersMetadata: []
  ): IComputationOutput => {

  const unfilteredDataWithoutPct: IDatumWithoutPct[] = data.map(
    datum => ({
      id: getId(datum),
      value: getValue(datum),
    }),
  );
  // Filter out data points with non-positive value:
  const dataWithoutPct = unfilteredDataWithoutPct.filter(({value}) => value > 0);
  const allValues = dataWithoutPct.map(({value}) => value);
  const minValue = Math.min(...allValues);
  const maxValue = Math.max(...allValues);
  const [domainStart, domainEnd] = scaleLog().domain([minValue, maxValue]).nice().domain();
  const ordersOfMagnitudeDifference = getOrdersOfMagnitudeDifference(domainStart, domainEnd);
  const spectrumStartValue =
    10 ** (Math.log10(domainStart) + ordersOfMagnitudeDifference * spectrumStartPct);
  const spectrumEndValue =
    10 ** (Math.log10(domainStart) + ordersOfMagnitudeDifference * spectrumEndPct);
  const spectrumScale = scaleLog<string, string>()
                        .domain([spectrumStartValue, spectrumEndValue])
                        .range([colorRangeStart, colorRangeEnd]);
  const sumTotal = sum(allValues);


  /*
    Note: The API returns data for all countries except for the country ID itself;
    thus, `data` passed in above is already the set of all non-primary countries implicitly.
  */
  const nonPrimaryCountries: ICountryDatum[] = dataWithoutPct.map(datum => {
    const {value, id} = datum;
    const {name_short_en, name_en} = metadata.get(id)!;
    const percentage = value / sumTotal;

    let color: string;
    if (value > spectrumEndValue) {
      color = colorRangeEnd;
    } else if (value < spectrumStartValue) {
      color = colorRangeStart;
    } else {
      color = spectrumScale(value);
    }
    const countryDatumDisjoint: ICountryDatumDisjoint = {
      isPrimaryCountry: false,
      value, percentage, color,
    };

    const tradeLabel = getTradeLabel(tradeDirection, tradeFlow);
    const tradeInfo: IRow = {
      label: tradeLabel,
      value: formatTradeValue(value),
    };
    const shareInfo: IRow = {
      label: __lexiconText('applicationWide.share'),
      value: formatPercentage(percentage),
    };
    const yearInfo: IRow = {
      label: __lexiconText('applicationWide.year'),
      value: year,
    };
    const tooltipInfo = [tradeInfo, shareInfo];
    const detailOverlayInfo = [yearInfo, tradeInfo, shareInfo];
    const regionId = levelMapping.get(id)!;
    const {name_short_en: regionName} = metadata.get(regionId)!;
    const {regionName: mappedRegionName, groupId: mappedRegionId} = convertOldRegionsToNewRegions(regionName);
    return {
      ...countryDatumDisjoint,
      shortLabel: name_short_en,
      longLabel: name_en,
      // regionName,
      regionName: mappedRegionName,
      // regionColor: regionColorMap.get(regionId)!,
      regionColor: groupedRegionColorMap.get(mappedRegionId)!,

      id, tooltipInfo, detailOverlayInfo,
    };
  });


  let allCountries: ICountryDatum[];
  if (primaryCountryId === undefined) {
    allCountries = nonPrimaryCountries;
  } else {
    if(queryLevel === QueryLevel.Location) {
      const {
        name_en: primaryCountryShortLabel,
        name_short_en: primaryCountryLongLabel,
      } = metadata.get(primaryCountryId)!;
      const primaryCountry: ICountryDatum = {
        id: primaryCountryId,
        isPrimaryCountry: true,
        shortLabel: primaryCountryShortLabel,
        longLabel: primaryCountryLongLabel,
        regionColor: regionColorMap.get(levelMapping.get(primaryCountryId)),
        detailOverlayInfo: [],
        tooltipInfo: [],
        // Note: region name for "primary country" will never be shown:
        regionName: '',
      };
      allCountries = [...nonPrimaryCountries, primaryCountry];
    } else if(queryLevel === QueryLevel.Group) {

      let primaryLocationIdsInGroup = new Set();
      
      [...groupMembersMetadata.entries()].forEach(entry => {
        let locationId = entry[0];
        let locationMemberships = entry[1];
        locationMemberships.forEach(membership => {
          // If the group ID of the membership is equal to the primaryCountryId,
          // which in the case of queryLevel = Group is an alias for a group-# ID
          if(membership.group_id == primaryCountryId) {
            primaryLocationIdsInGroup.add(locationId);
          }
        });
      });

      const primaryCountriesInGroup = [...primaryLocationIdsInGroup].map(countryId => {
        const {
          name_en: primaryCountryShortLabel,
          name_short_en: primaryCountryLongLabel,
        } = metadata.get(countryId);
        
        return {
          id: countryId,
          isPrimaryCountry: true,
          shortLabel: primaryCountryShortLabel,
          longLabel: primaryCountryLongLabel,
          regionColor: regionColorMap.get(levelMapping.get(countryId)),
          detailOverlayInfo: [],
          tooltipInfo: [],
          // Note: region name for "primary country" will never be shown:
          regionName: '',
        };
  
      });

      allCountries = [...nonPrimaryCountries, ...primaryCountriesInGroup];
    }

  }

  const countryData = keyBy(allCountries, ({id}) => id);

  const legendData: ILegendData = {
    domainStart, domainEnd,
    domainSpectrumStart: spectrumStartValue,
    domainSpectrumEnd: spectrumEndValue,
    rangeStart: colorRangeStart,
    rangeEnd: colorRangeEnd,
  };

  const output: IComputationOutput = {
    countryData, legendData, width, height,
    total: sumTotal,
  };
  return output;
};

export default assignValueAndPct;
